import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Brain, ArrowRight, ArrowLeft, Lock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const PHASE_LANDING = "landing";
const PHASE_CONSENT = "consent";
const PHASE_QUESTION = "question";
const PHASE_DONE = "done";
const PHASE_ALREADY = "already";

export default function Quiz() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Admin preview mode: bypasses the "published" gate, the "already
  // submitted" gate, and the real submit endpoint - so admins can walk
  // through the full delegate experience before going live.
  const isAdminPreview = params.get("preview") === "1" && user?.role === "admin";
  const [config, setConfig] = useState(null);
  const [phase, setPhase] = useState(PHASE_LANDING);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // {question_id: answer_key}
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    api.get("/quiz/config")
      .then(({ data }) => { setConfig(data); setFeedback(data.feedback || ""); })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    if (!user || isAdminPreview) return;
    api.get("/quiz/me")
      .then(({ data }) => { if (data.submitted) setPhase(PHASE_ALREADY); })
      .catch(() => {});
  }, [user, isAdminPreview]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-10 text-center px-4">
        <Lock size={36} weight="duotone" className="mx-auto text-[#000000] mb-3" />
        <h1 className="font-display text-2xl mb-2">DFTB Conference Quiz</h1>
        <p className="text-sm text-[#5a5a5a] mb-4">Please sign in to take the quiz.</p>
        <Button onClick={() => navigate("/login?next=/quiz")} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="quiz-signin-btn">Sign in</Button>
      </div>
    );
  }

  if (!config) return <div className="py-10 text-center text-[#7A828C]">Loading…</div>;

  const questions = config.questions || [];
  const options = config.options || [];
  const total = questions.length;
  const ready = !!config.published || isAdminPreview;

  if (phase === PHASE_ALREADY) {
    return <CompletePage feedback={feedback} repeat />;
  }

  if (phase === PHASE_DONE) {
    return <CompletePage feedback={feedback} />;
  }

  if (phase === PHASE_LANDING) {
    return (
      <Wrapper>
        {isAdminPreview && <AdminPreviewBanner />}
        <Brain size={36} weight="duotone" className="text-[#000000] mb-3" />
        <h1 className="font-display text-3xl mb-2" data-testid="quiz-title">DFTB Conference Quiz</h1>
        <p className="text-sm text-[#5a5a5a] mb-4">
          A 10-question research study for delegates. Each question shows a clinical photograph with a short history.
          You&apos;ll choose the most likely mechanism from four options. Takes ~5 minutes.
        </p>
        <p className="text-xs text-[#7A828C] mb-6">You can only submit once.</p>
        {!ready ? (
          <div className="p-3 bg-[#FFF8E5] border border-[#E8D58A] rounded-sm text-sm text-[#5a4a00] mb-4" data-testid="quiz-not-ready">
            The quiz is not yet available - questions are still being prepared. Please check back shortly.
          </div>
        ) : (
          <Button onClick={() => setPhase(PHASE_CONSENT)} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="quiz-start-btn">
            Start the quiz <ArrowRight size={16} className="ml-1" />
          </Button>
        )}
      </Wrapper>
    );
  }

  if (phase === PHASE_CONSENT) {
    return (
      <Wrapper>
        {isAdminPreview && <AdminPreviewBanner />}
        <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-1">Before you start</div>
        <h1 className="font-display text-2xl mb-3">DFTB Conference Quiz - Consent and Data Use</h1>

        <div className="text-sm text-[#1c1c24] leading-relaxed space-y-4 mb-6">
          <section data-testid="consent-participant-information">
            <h2 className="font-display text-base text-[#000000] mb-1">Participant Information</h2>
            <p>You are invited to take part in a short educational quiz as part of the DFTB Conference. The purpose of this activity is to explore how healthcare professionals recognise patterns of injury and illness.</p>
          </section>

          <section data-testid="consent-data-collected">
            <h2 className="font-display text-base text-[#000000] mb-1">What data will be collected?</h2>
            <p className="mb-2">If you choose to participate, the following information will be collected:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your name and email address (as part of conference registration/login and as a link to response. Your personal details will not be saved or used in any data analysis or publication).</li>
              <li>Your professional role (if available via registration data).</li>
              <li>Your responses to the quiz questions.</li>
            </ul>
            <p className="mt-2">No additional personal or sensitive information will be requested as part of this activity.</p>
          </section>

          <section data-testid="consent-data-use">
            <h2 className="font-display text-base text-[#000000] mb-1">How will your data be used?</h2>
            <p className="mb-2">Your data will be used for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Educational evaluation of the quiz.</li>
              <li>Analysis of response patterns across participants.</li>
              <li>Development of summary findings for teaching and awareness purposes.</li>
            </ul>
            <p className="mt-2 mb-2">Findings may be shared in:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Educational materials.</li>
              <li>Conference presentations.</li>
              <li>Publications or reports.</li>
            </ul>
            <p className="mt-2">All results will be anonymised, and no individual participant will be identifiable in any outputs.</p>
          </section>

          <section data-testid="consent-storage">
            <h2 className="font-display text-base text-[#000000] mb-1">Data storage and security</h2>
            <p>Your data will be stored securely in accordance with UK data protection legislation (including the UK GDPR and Data Protection Act 2018). It will be accessible only to authorised members of the project team and retained only for as long as necessary for the purposes outlined above.</p>
          </section>

          <section data-testid="consent-voluntary">
            <h2 className="font-display text-base text-[#000000] mb-1">Voluntary participation</h2>
            <p>Participation in this quiz is entirely voluntary. You are free to withdraw at any time before submitting your responses by exiting the quiz. Once responses are submitted and anonymised, it may not be possible to remove your data from aggregated analyses.</p>
          </section>

          <section data-testid="consent-confidentiality">
            <h2 className="font-display text-base text-[#000000] mb-1">Confidentiality</h2>
            <p>All identifiable information will be handled confidentially. Any reporting will use anonymised data only.</p>
          </section>

          <section data-testid="consent-further-info">
            <h2 className="font-display text-base text-[#000000] mb-1">Further information</h2>
            <p>If you have any questions about this activity or how your data will be used, please contact the project team via the conference organisers.</p>
          </section>

          <section data-testid="consent-statement" className="p-3 bg-[#E6F8FC] border border-[#000000]/20 rounded-sm">
            <h2 className="font-display text-base text-[#000000] mb-1">Consent statement</h2>
            <p className="mb-2">By proceeding to the quiz, you confirm that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You have read and understood the information above.</li>
              <li>You voluntarily agree to take part.</li>
              <li>You consent to the use of your anonymised data for the purposes described.</li>
            </ul>
          </section>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => navigate("/info")}
            variant="outline"
            className="rounded-sm h-12 flex-1"
            data-testid="quiz-consent-decline-btn"
          >
            I do not wish to take part
          </Button>
          <Button
            onClick={() => { setIdx(0); setPhase(PHASE_QUESTION); }}
            className="rounded-sm h-12 flex-1 bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
            data-testid="quiz-consent-agree-btn"
          >
            I agree and wish to proceed <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </Wrapper>
    );
  }

  // PHASE_QUESTION
  const q = questions[idx];
  const chosen = answers[q.id];
  const goNext = async () => {
    if (!chosen) return toast.error("Pick an answer first");
    if (idx + 1 < total) {
      setIdx(idx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      if (isAdminPreview) {
        // Skip the real submit so admin previews don't pollute the data set
        // or count against the one-submission-per-user limit.
        await new Promise((r) => setTimeout(r, 300));
      } else {
        await api.post("/quiz/submit", { consent: true, answers });
      }
      setPhase(PHASE_DONE);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  const goBack = () => { if (idx > 0) setIdx(idx - 1); };

  return (
    <Wrapper>
      {isAdminPreview && <AdminPreviewBanner />}
      <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-1" data-testid="quiz-progress">
        Question {idx + 1} of {total}
      </div>
      <div className="w-full h-1 bg-[#E5E9F0] rounded-sm mb-4 overflow-hidden">
        <div className="h-full bg-[#000000] transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
      </div>
      <h2 className="font-display text-xl leading-tight mb-3" data-testid="quiz-question-prompt">{q.prompt}</h2>
      {q.has_image && (
        <LazyQuizImage
          questionId={q.id}
          number={q.number}
          className="w-full max-h-[420px] object-contain rounded-sm border border-[#E5E9F0] bg-[#F8F8FB] mb-3"
        />
      )}
      {q.clinical_history && (
        <div className="text-sm text-[#5a5a5a] leading-relaxed mb-4 whitespace-pre-line" data-testid="quiz-question-history">
          {q.clinical_history}
        </div>
      )}
      <fieldset className="space-y-2 mb-5" data-testid="quiz-question-options">
        <legend className="sr-only">Answer options</legend>
        {options.map((opt) => {
          const picked = chosen === opt.key;
          return (
            <label
              key={opt.key}
              className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${picked ? "border-[#000000] bg-[#E6F8FC]" : "border-[#E5E9F0] hover:border-[#000000]/50"}`}
              data-testid={`quiz-option-${opt.key}`}
            >
              <input
                type="radio"
                name={`q-${q.id}`}
                value={opt.key}
                checked={picked}
                onChange={() => setAnswers((cur) => ({ ...cur, [q.id]: opt.key }))}
                className="mt-1"
              />
              <span className="text-sm leading-snug text-[#1c1c24]">{opt.label}</span>
            </label>
          );
        })}
      </fieldset>
      <div className="flex items-center justify-between gap-2">
        <Button onClick={goBack} variant="outline" disabled={idx === 0} className="rounded-sm" data-testid="quiz-prev-btn">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <Button onClick={goNext} disabled={!chosen || submitting} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="quiz-next-btn">
          {idx + 1 === total ? (submitting ? "Submitting…" : "Submit") : "Next"} <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="quiz-page">
      {children}
    </div>
  );
}

function ConsentForm() { return null; } // legacy stub - the consent UI now lives inline in the PHASE_CONSENT branch above.

function AdminPreviewBanner() {
  return (
    <div
      data-testid="quiz-admin-preview-banner"
      className="mb-4 -mx-4 sm:mx-0 p-3 bg-[#FFF8E5] border-y sm:border border-[#E8D58A] sm:rounded-sm text-xs text-[#5a4a00]"
    >
      <strong className="font-display text-sm text-[#7a5a00]">Admin preview mode.</strong>{" "}
      You&apos;re seeing the delegate experience. Your submission will <em>not</em> be saved, and
      the publish gate is bypassed so you can test before going live.
    </div>
  );
}

function CompletePage({ feedback, repeat = false }) {
  return (
    <Wrapper>
      <CheckCircle size={36} weight="duotone" className="text-[#000000] mb-3" />
      <h1 className="font-display text-2xl mb-2" data-testid="quiz-complete-heading">
        {repeat ? "You've already submitted" : "Thanks for taking part!"}
      </h1>
      <p className="text-sm text-[#5a5a5a] mb-4">
        {repeat
          ? "You can only submit once. Your earlier responses are saved with the research team."
          : "Your responses are saved. The full answers and learning points will be published here after the conference."}
      </p>
      {feedback && (
        <div className="mt-4 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid="quiz-feedback">
          <h2 className="font-display text-lg mb-2">Answers &amp; learning points</h2>
          <div className="text-sm text-[#1c1c24] leading-relaxed whitespace-pre-line">{feedback}</div>
        </div>
      )}
    </Wrapper>
  );
}


// Lazy fetches a single quiz question's base64 image URL on mount so the
// `/quiz/config` response never has to ship multi-MB of inlined images. Hitting
// the bulk endpoint with all 10 images was the cause of the intermittent
// Cloudflare 520s on the live conference day.
function LazyQuizImage({ questionId, number, className }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/quiz/questions/${questionId}/image`)
      .then((r) => { if (active) setSrc(r.data.image_url || ""); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [questionId]);
  if (err) return null;
  if (!src) return (
    <div className={className} data-testid="quiz-question-image-loading">
      <div className="w-full h-48 grid place-items-center text-[10px] text-[#7A828C] font-mono-time uppercase tracking-widest">Loading image…</div>
    </div>
  );
  return (
    <img src={src} alt={`Question ${number}`} className={className} data-testid="quiz-question-image" />
  );
}
